import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Alert,
  AlertTitle
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Error as ErrorIcon } from '@mui/icons-material';
import { useErrorLogsQuery } from '../../API/ErrorLogs';
import CustomPagination from '@components/Common/table/CustomPagination';
import dayjs from 'dayjs';
import { defaultParams } from '@utils/global.constants';

interface ErrorLog {
  id: number;
  message: string;
  status: number;
  stack: string;
  path: string;
  method: string;
  time: string;
}

const ErrorLogs = () => {
  const [params, setParams] = useState({
    ...defaultParams,
    page: 1,
    limit: 50
  });
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { data: errorData, isLoading, isFetching, error } = useErrorLogsQuery({
    params,
    config: { keepPreviousData: true }
  });

  const errors = errorData?.data || [];
  const pagination = errorData?.pagination || {};

  const handlePageChange = (page: number) => {
    setParams({ ...params, page });
  };

  const toggleRowExpansion = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getStatusColor = (status: number) => {
    if (status >= 500) return 'error';
    if (status >= 400) return 'warning';
    return 'default';
  };

  const getMethodColor = (method: string) => {
    const colors: any = {
      GET: '#4caf50',
      POST: '#2196f3',
      PUT: '#ff9800',
      DELETE: '#f44336',
      PATCH: '#9c27b0'
    };
    return colors[method] || '#757575';
  };

  if (error) {
    return (
      <div className="main-dashboard-div">
        <div className="dashboard-wrapper">
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load error logs. Please try again later.
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="main-dashboard-div">
      <div className="dashboard-wrapper">
        <div className="heading-dashboard" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <ErrorIcon color="error" />
          <h2>Error Logs</h2>
        </div>

        {/* Summary Box */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Paper sx={{ p: 2, flex: 1, backgroundColor: '#fff3e0' }}>
            <Typography variant="h6" color="text.secondary">Total Errors</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff6f00' }}>
              {pagination.total || 0}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, backgroundColor: '#ffebee' }}>
            <Typography variant="h6" color="text.secondary">Server Errors (5xx)</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
              {errors.filter((e: ErrorLog) => e.status >= 500).length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, backgroundColor: '#fff8e1' }}>
            <Typography variant="h6" color="text.secondary">Client Errors (4xx)</Typography>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
              {errors.filter((e: ErrorLog) => e.status >= 400 && e.status < 500).length}
            </Typography>
          </Paper>
        </Box>

        {/* Error Table */}
        <TableContainer component={Paper}>
          {isLoading || isFetching ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading error logs...</Typography>
            </Box>
          ) : errors.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No error logs found</Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell width="50px"></TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Path</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((errorLog: ErrorLog) => (
                  <React.Fragment key={errorLog.id}>
                    <TableRow 
                      sx={{ 
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        cursor: 'pointer'
                      }}
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(errorLog.id)}
                        >
                          {expandedRows.has(errorLog.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(errorLog.time).format('MMM DD, YYYY')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(errorLog.time).format('HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={errorLog.status}
                          color={getStatusColor(errorLog.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={errorLog.method}
                          size="small"
                          sx={{ 
                            backgroundColor: getMethodColor(errorLog.method),
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={errorLog.path}
                        >
                          {errorLog.path}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2"
                          sx={{ 
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={errorLog.message}
                        >
                          {errorLog.message}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedRows.has(errorLog.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Error Details
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Full Message:
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                {errorLog.message}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary">
                                Stack Trace:
                              </Typography>
                              <Box 
                                sx={{ 
                                  mt: 1,
                                  p: 2,
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: 1,
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-all',
                                  maxHeight: '300px',
                                  overflow: 'auto'
                                }}
                              >
                                {errorLog.stack}
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        {!isLoading && errors.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <CustomPagination
              totalPages={pagination.totalPages || Math.ceil((pagination.total || 0) / params.limit)}
              page={pagination.page || params.page}
              handlePage={handlePageChange}
            />
          </Box>
        )}
      </div>
    </div>
  );
};

export default ErrorLogs;